import React from "react";
import { cn } from "@/lib/utils";
import Button, { ButtonType } from "../reusable/Button";
import ResponsiveImage from "../ui/ResponsiveImage";
import { getIconUrl } from "@/lib/directus";
import { LazyMotion, domAnimation, m } from "framer-motion";
import TextButton from "../ui/TextButton";

interface ColumnTextImageProps {
  data: {
    textAlignment: "left" | "right";
    name: string;
    role: string;
    description: string;
    image: any[];
    buttonText?: string;
    buttonLink?: string;
    sectionName?: string;
  };
}

const fadeIn = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

export default function ColumnTextImage({
  data,
}: {
  data: ColumnTextImageProps["data"];
}) {
  const sectionId = data.sectionName && data.sectionName.replace(/\s+/g, "-");
  const isRight = data.textAlignment === "right";

  const textSection = (
    <m.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      variants={fadeIn}
      className={cn(
        "pt-8",
        "desktop:pt-0",
        "flex flex-col justify-center",
        "w-full max-w-[85vw] desktop:max-w-[51vw]",
        isRight
          ? "desktop:pl-[7.5vw] desktop:pr-0"
          : "desktop:pr-[7.5vw] desktop:pl-0"
      )}
    >
      <h2 className="font-serif text-h2-mobile md:text-h2-tablet desktop:text-h2-desktop mb-4 text-accent-color-two">
        {data.name}
      </h2>
      <div className="uppercase tracking-widest font-semibold mb-4 text-white text-[18px]">
        {data.role}
      </div>
      <div
        className="text-white font-sans text-paragraph-large mb-8 max-w-3xl"
        dangerouslySetInnerHTML={{ __html: data.description }}
      />
      {data.buttonText && data.buttonLink && (
        <TextButton
          text={data.buttonText}
          textColor="primary"
          link={data.buttonLink}
          className="leading-6 tracking-[0.15em] text-[18px] w-fit"
        />
      )}
    </m.div>
  );

  const imageSection = (
    <m.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      variants={fadeIn}
      className="relative w-full max-w-[85vw] desktop:max-w-[34vw] min-h-[350px] aspect-[3/4]"
    >
      <ResponsiveImage
        desktop_image={data.image[0].item.desktop_image}
        tablet_image={data.image[0].item.tablet_image}
        mobile_image={data.image[0].item.mobile_image}
        alt={data.image[0].item.imageAlt ?? data.name}
        fill
        className="object-cover "
        priority={false}
      />
    </m.div>
  );

  return (
    <LazyMotion features={domAnimation}>
      <section
      id={sectionId?.replaceAll(/[^a-zA-Z0-9-_]/g, "-")}
      data-name={data.sectionName}
      className="w-screen bg-accent-color-three flex justify-center min-h-[400px] pt-8 pb-8"
    >
      <div className="flex flex-col desktop:flex-row w-full max-w-[85vw] min-h-[400px]">
        <div className="flex flex-col desktop:hidden w-full">
          {imageSection}
          {textSection}
        </div>
        <div className="hidden desktop:flex flex-row w-full">
          {isRight ? (
            <>
              {imageSection}
              {textSection}
            </>
          ) : (
            <>
              {textSection}
              {imageSection}
            </>
          )}
        </div>
      </div>
    </section>
    </LazyMotion>
  );
}
